import type { AudioAsset, AudioSourceData } from './AudioAsset';

/**
 * Events that can trigger SFX on assets
 */
export enum AssetSfxEvent {
    /** SFX plays when the asset first becomes visible (start of fade-in or appear) */
    ENTER = 'enter',
}

/**
 * Events that can trigger SFX on scenes
 */
export enum SceneSfxEvent {
    /** SFX plays when the scene starts */
    SCENE_START = 'scene_start',
    /** SFX plays at the midpoint of the scene's transition-in. If there is no transition, this is the same as SCENE_START */
    TRANSITION_IN_MIDPOINT = 'transition_in_midpoint',
}

/**
 * Common properties shared by all SFX items
 */
type BaseSfxItem = {
    /** Unique identifier for the SFX item */
    id: string;
    /** Audio source data for the SFX */
    sourceData: AudioSourceData;
    /** Offset in milliseconds from the event (can be negative) */
    offset?: number;
};

/**
 * Represents a sound effect attached to an asset
 */
export type AssetSfxItem = BaseSfxItem & {
    /** The event that triggers this SFX */
    event: AssetSfxEvent;
};

/**
 * Represents a sound effect attached to a scene
 */
export type SceneSfxItem = BaseSfxItem & {
    /** The event that triggers this SFX */
    event: SceneSfxEvent;
};

/**
 * Represents a sound effect attached to an asset or scene
 */
export type SfxItem = AssetSfxItem | SceneSfxItem;

/**
 * Interface for objects that can own SFX (scenes and assets).
 * Provides type safety for SFX-related operations.
 */
export interface SfxOwner {
    /** The list of SFX attached to this owner */
    readonly sfx: readonly SfxItem[];
    /** Add a sound effect to this owner */
    addSfx(settings: AddSfxSettings): SfxItem;
    /** Remove a sound effect by its ID */
    removeSfx(id: string): void;
    /** Get a sound effect by its ID */
    getSfxById(id: string): SfxItem | undefined;
    /** Get the AudioAsset for an SFX by its ID */
    _getSfxAudioAsset(id: string): AudioAsset | undefined;
    /** Calculate the global playback time for an SFX */
    _getSfxPlaybackTime(sfx: SfxItem): number;
    /** Load SFX data from serialization */
    _sfxCustomLoad(data: SfxItem[] | null): void;
}

/**
 * Represents a sound effect with its owner and calculated playback time
 */
export type SfxResult = {
    /** The slide or asset that owns this SFX */
    owner: SfxOwner;
    /** The SFX item */
    sfx: SfxItem;
    /** The global playback time in milliseconds */
    playbackTime: number;
};

/**
 * Common settings shared by all SFX types
 */
type BaseSfxSettings = {
    /** Audio source data for the SFX */
    sourceData: AudioSourceData;
    /** Offset in milliseconds from the event (can be negative, default: 0) */
    offset?: number;
};

/**
 * Settings for adding a new SFX item to an asset
 */
export type AddAssetSfxSettings = BaseSfxSettings & {
    /** The event that triggers this SFX */
    event: AssetSfxEvent;
};

/**
 * Settings for adding a new SFX item to a scene
 */
export type AddSceneSfxSettings = BaseSfxSettings & {
    /** The event that triggers this SFX */
    event: SceneSfxEvent;
};

/**
 * Settings for adding a new SFX item (asset or scene)
 */
type AddSfxSettings = AddAssetSfxSettings | AddSceneSfxSettings;
